import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { CongresoFaq } from '../types/congreso.types';

interface FaqItem {
  pregunta: string;
  respuesta: string;
}

const FAQS_DEFAULT: FaqItem[] = [
  {
    pregunta: '¿Cómo y dónde adquiero mi entrada con arancel subsidiado?',
    respuesta: 'Podés adquirir tu entrada directamente en la sede de tu Colegio Departamental o ingresando a la plataforma oficial de Autogestión con tu usuario y contraseña de matriculado.'
  },
  {
    pregunta: '¿Quiénes participan del sorteo del Auto 0KM?',
    respuesta: 'Participan todos los martilleros y corredores públicos matriculados de la Provincia de Buenos Aires que se encuentren habilitados reglamentariamente y asistan y acrediten su presencia durante las jornadas del Congreso.'
  },
  {
    pregunta: '¿Puedo transferir o nominar mi entrada a otra persona?',
    respuesta: 'Sí. Las entradas pueden nominarse a nombre del titular o de un tercero/acompañante desde la plataforma digital de Autogestión antes del inicio del evento, o bien en la mesa de acreditación en puerta.'
  },
  {
    pregunta: '¿Habrá venta de entradas en la puerta del evento?',
    respuesta: 'Sí, contaremos con mesas de venta en puerta en el Salón del Paseo Aldrey, donde podrás abonar en Efectivo o con tarjeta mediante terminales POS de Mercado Pago a la tarifa general base, sujeto a la capacidad remanente de la sala.'
  },
  {
    pregunta: '¿Se entrega certificado de asistencia?',
    respuesta: 'Sí. Una vez finalizado el Congreso, todos los asistentes acreditados podrán descargar su certificado oficial de asistencia con firma digital y código de validación QR directamente desde la App móvil del Congreso y el Portal de Autogestión con su matrícula.'
  }
];

interface FaqAccordionProps {
  faqs?: CongresoFaq[];
}

export const FaqAccordion: React.FC<FaqAccordionProps> = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const listaFaqs = faqs && faqs.length > 0
    ? faqs.filter(f => f.activo).sort((a, b) => a.orden - b.orden)
    : FAQS_DEFAULT;

  return (
    <section id="faq" className="py-24 bg-slate-50 relative border-t border-slate-200/80">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-50 border border-brand-cyan/30 text-brand-navy text-xs font-bold uppercase tracking-wider mb-4">
            <HelpCircle className="w-3.5 h-3.5 text-brand-cyan" />
            Preguntas Frecuentes
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-brand-navy tracking-tight">
            Resolvé tus Dudas
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            Todo lo que necesitás saber sobre el acceso, acreditaciones, sedes y reglamentación.
          </p>
        </div>

        {/* Acordeón */}
        <div className="space-y-4">
          {listaFaqs.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden border border-slate-200/90 shadow-sm hover:border-brand-cyan/40 transition-all duration-200"
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full p-6 text-left flex items-center justify-between gap-4 font-heading font-bold text-base sm:text-lg text-brand-navy hover:text-brand-cyan transition-colors"
                  aria-expanded={isOpen}
                >
                  <span>{item.pregunta}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-brand-cyan flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 pt-3 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100 bg-slate-50/50">
                    {item.respuesta}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
